import { PlusOutlined } from "@ant-design/icons";
import { Card, Col, Collapse, Row } from "antd";
import cuid from "cuid";
import { find, get, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { BiCog, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import ConditionsEditor from "shared/components/ConditionsEditor";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import {
  DOCUMENT_TEMPLATE_STATES,
  GENERATED_DOCUMENTS_TYPES,
} from "utils/constants";
import {
  areaOfMoment,
  documentState,
  MOMENT_OPTIONS,
  momentOfArea,
  normalizeArea,
} from "utils/helpers/documentTemplates";
import { toApiDocumentTemplate } from "utils/helpers/generatedDocumentsConfig";

const EMPTY_CONDITIONS = { operator: "AND", rules: [] };

const typeOptions = GENERATED_DOCUMENTS_TYPES.map((value) => ({
  value,
  label: value,
}));

const toApiLink = (row) => {
  const link = toApiDocumentTemplate(row);
  if (isNil(link)) return null;
  return { ...link, area: normalizeArea(row.area) };
};

const DocumentTemplatesManager = ({
  documentTemplates,
  planId,
  setFieldValue,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [updatePlan] = useMutation(graphql.mutations.UPDATE_INSURANCE_PLAN);
  const { data } = useQuery(graphql.queries.DOCUMENT_TEMPLATES, {
    variables: { take: 500, skip: 0 },
  });

  const areaOptions = MOMENT_OPTIONS.map(({ value }) => ({
    value,
    label: t(`CONFIGURE.DOCUMENT_TEMPLATES.AREA.OPTIONS.${value}`),
  }));

  const rows = documentTemplates || [];
  const templates = get(data, "documentTemplates.data") || [];
  const templateOptions = templates.map((template) => ({
    value: template.id,
    label:
      documentState(template) === DOCUMENT_TEMPLATE_STATES.DRAFT
        ? t("CONFIGURE.DOCUMENT_TEMPLATES.TEMPLATE_DRAFT", {
            name: template.name,
          })
        : template.name,
  }));

  const persist = (next) => {
    setFieldValue("documentTemplates", next);
    updatePlan({
      variables: {
        where: { id: planId },
        data: {
          documentTemplates: next.map(toApiLink).filter(Boolean),
        },
      },
    });
  };

  const updateRow = (index, patch) =>
    persist(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const getConditions = (row) => row.conditions || EMPTY_CONDITIONS;

  const updateConditions = (index, conditions) =>
    updateRow(index, { conditions });

  const addRow = () =>
    setFieldValue("documentTemplates", [
      ...rows,
      {
        id: cuid(),
        templateId: "",
        type: "",
        area: null,
        outputName: "",
        conditions: EMPTY_CONDITIONS,
      },
    ]);

  const removeRow = (index) => persist(rows.filter((_, i) => i !== index));

  return (
    <Card
      title={t("CONFIGURE.DOCUMENT_TEMPLATES.TITLE")}
      className="m-bottom-16"
    >
      <p className="m-bottom-16">
        {t("CONFIGURE.DOCUMENT_TEMPLATES.AREA.HELP")}
      </p>
      {rows.map((row, index) => {
        const conditions = getConditions(row);
        const rulesCount = get(conditions, "rules.length", 0);

        return (
          <div className="m-bottom-16" key={row.id || row.templateId || index}>
            <Row gutter={[16, 16]} align="middle">
              <Col span={6}>
                <Select
                  label={t("CONFIGURE.DOCUMENT_TEMPLATES.TEMPLATE")}
                  placeholder={t("CONFIGURE.DOCUMENT_TEMPLATES.TEMPLATE")}
                  options={templateOptions}
                  value={row.templateId || undefined}
                  onChange={(value) =>
                    updateRow(index, {
                      templateId: value,
                      templateName: get(
                        find(templates, { id: value }),
                        "name",
                        "",
                      ),
                    })
                  }
                />
              </Col>
              <Col span={5}>
                <Select
                  label={t("CONFIGURE.DOCUMENT_TEMPLATES.TYPE")}
                  placeholder={t("CONFIGURE.DOCUMENT_TEMPLATES.TYPE")}
                  options={typeOptions}
                  value={row.type || undefined}
                  onChange={(value) => updateRow(index, { type: value })}
                />
              </Col>
              <Col span={5}>
                <Select
                  label={t("CONFIGURE.DOCUMENT_TEMPLATES.AREA.LABEL")}
                  placeholder={t("CONFIGURE.DOCUMENT_TEMPLATES.AREA.LABEL")}
                  options={areaOptions}
                  allowClear
                  value={momentOfArea(row.area)?.value}
                  onChange={(moment) =>
                    updateRow(index, { area: areaOfMoment(moment) })
                  }
                />
              </Col>
              <Col span={5}>
                <Input
                  label={t("CONFIGURE.DOCUMENT_TEMPLATES.OUTPUT_NAME")}
                  placeholder={t("CONFIGURE.DOCUMENT_TEMPLATES.OUTPUT_NAME")}
                  value={row.outputName || ""}
                  onChange={({ target: { value } }) =>
                    updateRow(index, { outputName: value })
                  }
                />
              </Col>
              <Col span={3}>
                <BiTrash
                  color={COLORS.C_DANGER}
                  size={24}
                  className="cursor--pointer"
                  onClick={() => removeRow(index)}
                />
              </Col>
            </Row>

            <Collapse
              className="m-top-8"
              items={[
                {
                  key: `conditions-${row.id || index}`,
                  label: (
                    <span>
                      <BiCog size={14} className="mr-6" />
                      {t("CONFIGURE.DOCUMENT_TEMPLATES.CONDITIONS")}
                      {rulesCount > 0 && ` (${rulesCount})`}
                    </span>
                  ),
                  children: (
                    <ConditionsEditor
                      conditions={conditions}
                      emptyMessage={t(
                        "CONFIGURE.DOCUMENT_TEMPLATES.NO_CONDITIONS",
                      )}
                      fieldPlaceholder={t(
                        "CONFIGURE.DOCUMENT_TEMPLATES.FIELD_PLACEHOLDER",
                      )}
                      onOperatorChange={(operator) =>
                        updateConditions(index, { ...conditions, operator })
                      }
                      onAddRule={() =>
                        updateConditions(index, {
                          ...conditions,
                          rules: [
                            ...conditions.rules,
                            { key: "", op: "equals", value: "" },
                          ],
                        })
                      }
                      onRemoveRule={(ruleIndex) =>
                        updateConditions(index, {
                          ...conditions,
                          rules: conditions.rules.filter(
                            (_, i) => i !== ruleIndex,
                          ),
                        })
                      }
                      onUpdateRule={(ruleIndex, patch) =>
                        updateConditions(index, {
                          ...conditions,
                          rules: conditions.rules.map((rule, i) =>
                            i === ruleIndex ? { ...rule, ...patch } : rule,
                          ),
                        })
                      }
                    />
                  ),
                },
              ]}
            />
          </div>
        );
      })}
      <Button type="primary" onClick={addRow}>
        <PlusOutlined color={COLORS.C_WHITE} />
        {t("CONFIGURE.DOCUMENT_TEMPLATES.ADD")}
      </Button>
    </Card>
  );
};

export default DocumentTemplatesManager;
